import { Avatar } from "components";
import { TABLE_DATE } from "consts";
import { dateTransform } from "helpers/dataHelpers";
import { CancelIcon, CheckCircleIcon, TextFieldsIcon } from "icons";
import { CallBlackIcon } from "icons/CallBlackIcon";
import { FlexContainer } from "styled/Box";
import { theme } from "theme";
import {
  EFilterDateLabel,
  EFilterDateValue,
  ELoyaltyLevelLabel,
  ELoyaltyLevelValue,
} from "types/common";
import {
  CustomerProfileResponseDto,
  CustomersFilterNames,
  CustomersFilterTypes,
} from "types/customers";
import {
  FilterField,
  FilterFieldDropdownOption,
  FilterOption,
} from "types/tableFilters";

export const columns = [
  {
    title: "Customer name",
    dataIndex: "fullName",
    key: "fullName",
    width: "355px",
    render: (name: string | null) =>
      name ? (
        <FlexContainer $align="center" $gap={8}>
          <Avatar name={name} /> {name}
        </FlexContainer>
      ) : null,
  },
  {
    title: "Phone number",
    dataIndex: "phoneNumber",
    key: "phoneNumber",
  },
  {
    title: "Birth date",
    dataIndex: "birthDate",
    key: "birthDate",
  },
  {
    title: "Coupons used",
    dataIndex: "couponUsageCount",
    key: "couponUsageCount",
    align: "center" as const,
  },
  {
    title: "Loyalty tier",
    dataIndex: "loyaltyData",
    key: "loyaltyData",
    width: 120,
    render: (loyaltyData: CustomerProfileResponseDto["loyaltyData"]) =>
      loyaltyData?.name,
  },
  {
    title: "Referral Link",
    dataIndex: "referralsCount",
    key: "referralsCount",
    align: "center" as const,
  },
  {
    title: "VIP Status",
    dataIndex: "isVip",
    key: "isVip",
    align: "center" as const,
    render: (isVip: boolean) =>
      isVip ? <CheckCircleIcon /> : <CancelIcon fill={theme.color.error} />,
  },
  {
    title: "Calories Bank",
    dataIndex: "isCaloriesBankEnabled",
    key: "isCaloriesBankEnabled",
    align: "center" as const,
    render: (isCaloriesBankEnabled: boolean) =>
      isCaloriesBankEnabled ? (
        <CheckCircleIcon />
      ) : (
        <CancelIcon fill={theme.color.error} />
      ),
  },
  {
    title: "Cold kitchen",
    dataIndex: "isColdKitchen",
    key: "isColdKitchen",
    align: "center" as const,
    render: (isColdKitchen: boolean) =>
      isColdKitchen ? (
        <CheckCircleIcon />
      ) : (
        <CancelIcon fill={theme.color.error} />
      ),
  },
  {
    title: "AI features",
    dataIndex: "isAIAssistantEnabled",
    key: "isAIAssistantEnabled",
    align: "center" as const,
    render: (isAIAssistantEnabled: boolean) =>
      isAIAssistantEnabled ? (
        <CheckCircleIcon />
      ) : (
        <CancelIcon fill={theme.color.error} />
      ),
  },
  {
    title: "Created at",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 180,
    sorter: true,
    render: (date: string) =>
      dateTransform({
        date,
        format: TABLE_DATE,
      }),
  },
  {
    title: "Updated at",
    dataIndex: "updatedAt",
    key: "updatedAt",
    width: 180,
    sorter: true,
    render: (date: string) =>
      dateTransform({
        date,
        format: TABLE_DATE,
      }),
  },
];

export const dateDropdownFields: FilterFieldDropdownOption[] = [
  {
    type: EFilterDateValue.TODAY,
    label: EFilterDateLabel[EFilterDateValue.TODAY],
  },
  {
    type: EFilterDateValue.YESTERDAY,
    label: EFilterDateLabel[EFilterDateValue.YESTERDAY],
  },
  {
    type: EFilterDateValue.IN_THE_LAST_7_DAYS,
    label: EFilterDateLabel[EFilterDateValue.IN_THE_LAST_7_DAYS],
  },
  {
    type: EFilterDateValue.IN_THE_LAST_30_DAYS,
    label: EFilterDateLabel[EFilterDateValue.IN_THE_LAST_30_DAYS],
  },
  {
    type: EFilterDateValue.IN_THE_LAST_90_DAYS,
    label: EFilterDateLabel[EFilterDateValue.IN_THE_LAST_90_DAYS],
  },
  {
    type: EFilterDateValue.IN_THE_12_MONTHS,
    label: EFilterDateLabel[EFilterDateValue.IN_THE_12_MONTHS],
  },
  {
    type: EFilterDateValue.CUSTOM,
    label: EFilterDateLabel[EFilterDateValue.CUSTOM],
  },
];

export const loyaltyLevelDropdownFields: FilterFieldDropdownOption[] = [
  {
    type: ELoyaltyLevelValue.BRONZE,
    label: ELoyaltyLevelLabel[ELoyaltyLevelValue.BRONZE],
  },
  {
    type: ELoyaltyLevelValue.SILVER,
    label: ELoyaltyLevelLabel[ELoyaltyLevelValue.SILVER],
  },
  {
    type: ELoyaltyLevelValue.GOLD,
    label: ELoyaltyLevelLabel[ELoyaltyLevelValue.GOLD],
  },
  {
    type: ELoyaltyLevelValue.PLATINUM,
    label: ELoyaltyLevelLabel[ELoyaltyLevelValue.PLATINUM],
  },
];

export const statusesDropdownFields: FilterFieldDropdownOption[] = [
  {
    type: "true",
    label: "VIP",
  },
  {
    type: "",
    label: "All",
  },
];

export const isColdKitchenDropdownFields: FilterFieldDropdownOption[] = [
  {
    type: "true",
    label: "Available",
  },
  {
    type: "false",
    label: "Disabled",
  },
  {
    type: "",
    label: "All",
  },
];

export const isAiFeaturesDropdownFields: FilterFieldDropdownOption[] = [
  {
    type: "true",
    label: "Available",
  },
  {
    type: "false",
    label: "Disabled",
  },
  {
    type: "",
    label: "All",
  },
];

export const isCaloriesBankFeaturesDropdownFields: FilterFieldDropdownOption[] =
  [
    {
      type: "true",
      label: "Available",
    },
    {
      type: "false",
      label: "Disabled",
    },
    {
      type: "",
      label: "All",
    },
  ];

export const filtersList: FilterOption[] = [
  {
    key: CustomersFilterTypes.firstName,
    icon: <TextFieldsIcon />,
    label: CustomersFilterNames[CustomersFilterTypes.firstName],
    field: FilterField.INPUT_SELECT,
  },
  {
    key: CustomersFilterTypes.lastName,
    icon: <TextFieldsIcon />,
    label: CustomersFilterNames[CustomersFilterTypes.lastName],
    field: FilterField.INPUT_SELECT,
  },
  {
    key: CustomersFilterTypes.phoneNumber,
    icon: <CallBlackIcon />,
    label: CustomersFilterNames[CustomersFilterTypes.phoneNumber],
    field: FilterField.INPUT_SELECT,
  },
  {
    key: CustomersFilterTypes.isVip,
    label: CustomersFilterNames[CustomersFilterTypes.isVip],
    field: FilterField.SELECT,
    options: statusesDropdownFields,
  },
  {
    key: CustomersFilterTypes.isColdKitchen,
    label: CustomersFilterNames[CustomersFilterTypes.isColdKitchen],
    field: FilterField.SELECT,
    options: isColdKitchenDropdownFields,
  },
  {
    key: CustomersFilterTypes.isColdKitchen,
    label: CustomersFilterNames[CustomersFilterTypes.isColdKitchen],
    field: FilterField.SELECT,
    options: isColdKitchenDropdownFields,
  },
  {
    key: CustomersFilterTypes.isAiEnabled,
    label: CustomersFilterNames[CustomersFilterTypes.isAiEnabled],
    field: FilterField.SELECT,
    options: isAiFeaturesDropdownFields,
  },
  {
    key: CustomersFilterTypes.isCaloriesBankEnabled,
    label: CustomersFilterNames[CustomersFilterTypes.isCaloriesBankEnabled],
    field: FilterField.SELECT,
    options: isCaloriesBankFeaturesDropdownFields,
  },
  //TODO: remove comment if BE is ready
  // {
  //   key: CustomersFilterTypes.lastOrderDate,
  //   label: CustomersFilterNames[CustomersFilterTypes.lastOrderDate],
  //   field: FilterField.MULTISELECT_SINGLE_DATE,
  //   options: dateDropdownFields,
  // },
  //TODO: remove comment if BE is ready
  // {
  //   key: CustomersFilterTypes.mostOrderValue,
  //   label: CustomersFilterNames[CustomersFilterTypes.mostOrderValue],
  //   field: FilterField.MULTISELECT_SINGLE_DATE,
  //   options: dateDropdownFields,
  // },
  {
    key: CustomersFilterTypes.createdAtAfter,
    label: CustomersFilterNames[CustomersFilterTypes.createdAtAfter],
    field: FilterField.MULTISELECT_SINGLE_DATE,
    options: dateDropdownFields,
  },
  //TODO: remove comment if BE is ready
  // {
  //   key: CustomersFilterTypes.loyaltyLevel,
  //   label: CustomersFilterNames[CustomersFilterTypes.loyaltyLevel],
  //   field: FilterField.SELECT,
  //   options: loyaltyLevelDropdownFields,
  // },
];
